/**
 * Scoreboard, payout strip and per-player earnings tracker.
 *
 * All three read from the shared pure functions in game-logic, so the numbers
 * shown here are computed by exactly the same code the server uses to award
 * them.
 */

import {
  gamePhase,
  totalPot,
  payoutAmounts,
  playerEarnings,
  potRemaining,
  winningSquares,
} from '../../shared/game-logic';
import { shortPeriodLabel } from '../../shared/sports';
import { onColor } from '../../shared/team-colors';
import type { GameState, QuarterIndex, Team, TeamKey } from '../../shared/types';
import { formatMoney } from '../lib/format';


export function Scoreboard({ state }: { state: GameState }) {
  const phase = gamePhase(state);
  // Show the live score if one is entered, otherwise the last locked quarter.
  const display = liveOrLatest(state);

  return (
    <section className="card" aria-label="Scoreboard">
      <div className="scoreboard">
        <TeamSide team={state.config.teams.home} side="home" score={display.home} />
        <div className="center">
          <div className="score-sep">–</div>
          <div className={`badge ${phase.kind === 'not-started' ? '' : 'badge-live'}`}>
            {phase.label}
            {display.live && phase.kind !== 'not-started' ? ' · Live' : ''}
          </div>
        </div>
        <TeamSide team={state.config.teams.away} side="away" score={display.away} />
      </div>
    </section>
  );
}

function liveOrLatest(state: GameState) {
  if (state.liveScore.home !== null && state.liveScore.away !== null) {
    return { home: state.liveScore.home, away: state.liveScore.away, live: true };
  }
  if (state.config.mode === 'everyscore') {
    const last = state.scoreLog.at(-1);
    if (last) return { home: last.home, away: last.away, live: false };
  }
  for (let i = 3; i >= 0; i--) {
    const s = state.scores[i]!;
    if (s.home !== null && s.away !== null) {
      return { home: s.home, away: s.away, live: false };
    }
  }
  return { home: 0, away: 0, live: false };
}

function TeamSide({
  team,
  side,
  score,
}: {
  team: Team;
  side: TeamKey;
  score: number;
}) {
  return (
    <div className={`score-team ${side}`}>
      {team.logoUrl ? (
        <img className="score-logo" src={team.logoUrl} alt="" width={44} height={44} />
      ) : (
        <div
          className="score-monogram"
          style={{ background: team.color, color: onColor(team.color) }}
          aria-hidden="true"
        >
          {team.abbr.slice(0, 3)}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="score-name">{team.name}</div>
        <div className="score-value mono">{score}</div>
      </div>
    </div>
  );
}

export function PayoutStrip({ state }: { state: GameState }) {
  if (state.config.mode === 'everyscore') {
    const paidCount = state.scoreLog.filter((s) => s.paid).length;
    const remaining = potRemaining(state);
    return (
      <section className="card" aria-label="Payouts">
        <h2 className="card-title">Payouts</h2>
        <div className="payout-strip">
          <div className="payout-card">
            <div className="payout-label">Every Score</div>
            <div className="payout-amount mono">
              {formatMoney(state.config.payouts.everyscore.perScore)}
            </div>
            <div className="payout-winner">
              {paidCount} score{paidCount === 1 ? '' : 's'} paid
            </div>
            <div className="payout-digits">{formatMoney(remaining)} remaining</div>
          </div>
          {state.finalTwist && <FinalTwistCard state={state} />}
        </div>
      </section>
    );
  }

  const amounts = payoutAmounts(state);
  const winners = winningSquares(state);
  const nameFor = (id: string | null) =>
    id ? (state.players.find((p) => p.id === id)?.name ?? 'Nobody') : 'Nobody';

  /*
   * Four quarters across one compact row rather than four large cards.
   *
   * The old layout wrapped to two rows of tall cards on a phone and pushed the
   * payouts well below the fold -- which is the wrong place for the number
   * people glance at mid-game. Everything here fits one line per quarter.
   */
  return (
    <section className="card" aria-label="Payouts">
      <div className="row" style={{ marginBottom: 12 }}>
        <h2 className="card-title" style={{ margin: 0 }}>
          Payouts
        </h2>
        <div className="spacer" />
        <span className="small muted">
          {formatMoney(totalPot(state))} pot · {formatMoney(state.config.squareCost)}/square
        </span>
      </div>

      <div className="payout-row">
        {/* One column per checkpoint: four for quarters, two for halves. */}
        {amounts.map((_, q) => {
          const hit = winners.find((w) => w.quarter === q);
          const isPotential = hit?.kind === 'potential';
          const decided = Boolean(hit) && !isPotential;
          return (
            <div
              key={q}
              className={`payout-q ${decided ? 'won' : ''} ${isPotential ? 'potential' : ''}`}
            >
              <span className="payout-q-label">{shortPeriodLabel(state.config.league, q)}</span>
              <span className="payout-q-amount mono">{formatMoney(amounts[q] ?? 0)}</span>
              <span className="payout-q-winner">
                {hit ? nameFor(hit.ownerId) : <span className="muted">&mdash;</span>}
              </span>
              {hit && (
                <span className="payout-q-digits mono">
                  {hit.homeDigit}&ndash;{hit.awayDigit}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FinalTwistCard({ state }: { state: GameState }) {
  const twist = state.finalTwist!;
  const nameFor = (id: string | null) =>
    id ? (state.players.find((p) => p.id === id)?.name ?? 'Nobody') : 'Nobody';
  return (
    <div className="payout-card won">
      <div className="payout-label">Final Twist</div>
      <div className="payout-amount mono">
        {formatMoney(twist.actualAmount)} / {formatMoney(twist.inverseAmount)}
      </div>
      <div className="payout-winner">
        {nameFor(twist.actualOwnerId)} · {nameFor(twist.inverseOwnerId)}
      </div>
      <div className="payout-digits">
        {state.config.teams.home.abbr} {twist.homeDigit} – {state.config.teams.away.abbr}{' '}
        {twist.awayDigit}
      </div>
    </div>
  );
}

export function EarningsTracker({ state }: { state: GameState }) {
  const rows = playerEarnings(state);
  if (rows.length === 0) {
    return (
      <section className="card">
        <h2 className="card-title">Players</h2>
        <p className="empty-state">No players yet.</p>
      </section>
    );
  }

  return (
    <section className="card" aria-label="Player earnings">
      <h2 className="card-title">Players</h2>
      <div className="tracker-grid">
        {rows.map(({ player, earned, potential, byQuarter }) => (
          <div
            key={player.id}
            className={`tracker-card ${earned + potential > 0 ? 'has-winnings' : ''}`}
          >
            <div className="tc-name" style={{ color: player.color }}>
              {player.name}
            </div>
            <div className="tc-squares">
              {player.count} square{player.count === 1 ? '' : 's'}
            </div>
            <div className="tc-winnings mono">
              {formatMoney(earned)}
              {potential > 0 && (
                <span className="small muted"> +{formatMoney(potential)} live</span>
              )}
            </div>
            {state.config.mode === 'standard' && (
              <div className="tc-squares mono">
                {byQuarter
                  .map((amount, i) => `Q${i + 1} ${formatMoney(amount)}`)
                  .join(' · ')}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
